import { api } from 'src/boot/axios';
import { defineStore } from 'pinia';
import { getResponseData } from 'utils';
import type { DeathNotification } from 'src/types/memberUpdate';

type State = {
  deathNotifications: DeathNotification[];
};

/**
 * Death-notification methods used by the "record a member as deceased" form.
 * Cached in localStorage and refreshed in the background on read, mirroring the
 * countries store. The lookup is server-cached for 24 hours and requires no
 * specific permission. See annual-returns-member-updates.md.
 */
export const useDeathNotificationsStore = defineStore('death-notifications', {
  state: (): State => ({
    deathNotifications: [],
  }),
  getters: {
    /** Only the entries the API marks active — what the selector should offer. */
    activeDeathNotifications(state): DeathNotification[] {
      return state.deathNotifications.filter((entry) => entry.active);
    },
  },
  actions: {
    cacheDeathNotifications(data: DeathNotification[]) {
      if (data) {
        localStorage.setItem('deathNotifications', JSON.stringify(data));
      }
    },
    fetchDeathNotificationData() {
      return api.get('/api/v1/death-notifications');
    },
    async getDeathNotifications() {
      // Load from cache.
      const cached = localStorage.getItem('deathNotifications');
      if (cached) {
        this.deathNotifications = JSON.parse(cached) as DeathNotification[];

        // Ensure we update.
        this.fetchDeathNotificationData().then((response) => {
          const data = getResponseData(response) as DeathNotification[];
          this.deathNotifications = data;
          this.cacheDeathNotifications(data);
        });
        return;
      }

      // No cache.
      const response = await this.fetchDeathNotificationData();
      const data = getResponseData(response) as DeathNotification[];
      this.deathNotifications = data;
      this.cacheDeathNotifications(data);
    },
    clear() {
      this.deathNotifications = [];
    },
  },
});
